import { useState, useRef, useCallback } from "react";
import TimeSlotButton from "./TimeSlotButton";
import { Skeleton } from "@shared/components/ui/skeleton";
import { TimeSlot } from "@features/booking";
import { Button } from "@shared/components/ui/button";
import { useTranslation } from "react-i18next";
import { sanitizeHtml } from "@shared/utils/sanitize";
import { verifyAttrs } from "@/shared/verify/core/contract";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@shared/components/ui/dialog";
import { ChevronLeft, ChevronRight } from "lucide-react";

/**
 * No-availability variant types based on column type from API
 */
type NoAvailabilityVariant =
  | 'NO_AVAILABILITY'
  | 'WAITING_LIST'
  | 'WALK_IN'
  | 'CUT_OFF'
  | 'CUT_OFF_WAITING_LIST'
  | 'CUT_OFF_WALK_IN'
  | 'PREFERRED_ONLY';

export function getNoAvailabilityVariant(
  columnType: string | null | undefined,
  isWalkInDay: boolean,
  isWaitingListDay: boolean,
  centerHasOnlyPreferred?: boolean,
): NoAvailabilityVariant {
  // Preferred-only takes priority — all slots are preferred, user doesn't have access
  if (centerHasOnlyPreferred) return 'PREFERRED_ONLY';

  const ct = (columnType ?? '').toLowerCase();
  const isCutOff = ct.includes('cut_off') || ct.includes('cutoff');

  // Walk-in and waiting-list are mutually exclusive by construction: the calendar-availability
  // endpoint emits 'walk-in' only when waiting-list is unavailable. We consume those flags
  // straight from the calendar (legacy home.js:3172-3173) so the variant cannot silently
  // collapse if a stray columnType happens to mention both strings.
  if (isCutOff && isWaitingListDay) return 'CUT_OFF_WAITING_LIST';
  if (isCutOff && isWalkInDay) return 'CUT_OFF_WALK_IN';
  if (isCutOff) return 'CUT_OFF';
  if (isWaitingListDay) return 'WAITING_LIST';
  if (isWalkInDay) return 'WALK_IN';
  return 'NO_AVAILABILITY';
}

export interface AlternativeMenu {
  id: number;
  name: string;
  image?: string;
  description?: string;
  details?: string;
  price?: string;
  times: string[];
}

interface AvailableTimesListProps {
  availableTimes: TimeSlot[];
  selectedTime: string | null;
  onSelectTime: (time: string) => void;
  loading?: boolean;
  columnType?: string | null;
  allowWaitingList?: boolean;
  /** True when the selected date/service is flagged as walk-in by the calendar (type === 'walk-in'). */
  isWalkInDay?: boolean;
  /** True when waiting list is enabled for the restaurant and the selected day is not WL-full. */
  isWaitingListDay?: boolean;
  onJoinWaitingList?: () => void;
  centerHasOnlyPreferred?: boolean;
  isAuthenticated?: boolean;
  onPreferredLogin?: () => void;
  alternativeMenus?: AlternativeMenu[];
  onSelectAlternativeMenu?: (menuId: number, time?: string) => void;
  onShowWalkInDetails?: () => void;
}

const skeletonCount = 5;

function NoAvailabilityMessage({
  variant,
  onJoinWaitingList,
  isAuthenticated,
  onPreferredLogin,
  allowWaitingList = false,
  alternativeMenus = [],
  onSelectAlternativeMenu,
  onShowWalkInDetails,
}: {
  variant: NoAvailabilityVariant;
  onJoinWaitingList?: () => void;
  isAuthenticated?: boolean;
  onPreferredLogin?: () => void;
  allowWaitingList?: boolean;
  alternativeMenus?: AlternativeMenu[];
  onSelectAlternativeMenu?: (menuId: number) => void;
  onShowWalkInDetails?: () => void;
}) {
  const { t } = useTranslation('booking');

  switch (variant) {
    case 'PREFERRED_ONLY':
      return (
        <div className="flex flex-col items-center py-8 px-6 max-w-md mx-auto">
          <p className="text-foreground font-medium text-lg mb-2 text-center">
            {t('RESTRICTED_AVAILABILITY')}
          </p>
          <p className="text-foreground/70 text-sm text-center mb-6">
            {isAuthenticated
              ? t('AVAILABLE_TABLES_PREFERRED_ACCESS')
              : t('AVAILABLE_TABLES_PREFERRED_ACCESS_LOGGED_OUT')}
          </p>
          {!isAuthenticated && onPreferredLogin && (
            <button
              type="button"
              onClick={onPreferredLogin}
              className="text-brand font-semibold text-sm underline hover:text-brand-dark transition-colors"
            >
              {t('CLICK_HERE_TO_LOGIN')}
            </button>
          )}
          {allowWaitingList && onJoinWaitingList && (
            <>
              <p className="text-foreground/70 text-sm text-center mt-6 mb-4">
                {t('WAITING_LIST_MESSAGE_PREFERRED_ACCESS')}
              </p>
              <Button
                className="w-full bg-brand hover:bg-brand-dark text-brand-foreground"
                size="lg"
                onClick={onJoinWaitingList}
              >
                {t('JOIN_THE_WAITING_LIST')}
              </Button>
            </>
          )}
        </div>
      );

    case 'WAITING_LIST':
      return (
        <div className="flex flex-col items-center py-8 px-6 max-w-md mx-auto">
          <p className="text-foreground font-medium text-lg mb-4 text-center">
            {t('NOT_AVAILABLE')}
          </p>
          <p className="text-foreground/70 text-sm text-center mb-6">
            {t('STRING_ALTERNATIVES_WAITING_LIST_EDIT')}
          </p>
          <Button
            className="w-full bg-brand hover:bg-brand-dark text-brand-foreground"
            size="lg"
            onClick={onJoinWaitingList}
          >
            {t('JOIN_THE_WAITING_LIST')}
          </Button>
        </div>
      );

    case 'WALK_IN':
      return (
        <div className="flex flex-col items-center py-8 px-6 max-w-md mx-auto">
          <p className="text-foreground font-medium text-lg mb-2">
            {t('WALK_IN_ONLY')}
          </p>
          {onShowWalkInDetails && (
            <button
              type="button"
              onClick={onShowWalkInDetails}
              className="text-foreground underline text-base hover:text-brand"
            >
              {t('NEW_MODAL_VIEW_DETAILS')}
            </button>
          )}
        </div>
      );

    case 'CUT_OFF':
      return (
        <div className="flex flex-col items-center py-8 px-6 max-w-md mx-auto">
          <p className="text-foreground font-medium text-lg mb-4">
            {t('TIME_RESERVATIONS_CLOSED')}
          </p>
          <p className="text-foreground/70 text-sm text-center">
            {t('CUT_OFF_MESSAGE')}
          </p>
        </div>
      );

    case 'CUT_OFF_WAITING_LIST':
      return (
        <div className="flex flex-col items-center py-8 px-6 max-w-md mx-auto">
          <p className="text-foreground font-medium text-lg mb-4">
            {t('TIME_RESERVATIONS_CLOSED')}
          </p>
          <p className="text-foreground/70 text-sm text-center mb-6">
            {t('CUT_OFF_WITH_WAITING_LIST_MESSAGE')}
          </p>
          <Button
            className="w-full bg-brand hover:bg-brand-dark text-brand-foreground"
            size="lg"
            onClick={onJoinWaitingList}
          >
            {t('JOIN_THE_WAITING_LIST')}
          </Button>
        </div>
      );

    case 'CUT_OFF_WALK_IN':
      return (
        <div className="flex flex-col items-center py-8 px-6 max-w-md mx-auto">
          <p className="text-foreground/70 text-sm text-center mb-6">
            {t('CUT_OFF_WITH_WALK_IN_MESSAGE')}
          </p>
          {onShowWalkInDetails && (
            <Button
              className="bg-brand hover:bg-brand-dark text-brand-foreground"
              size="lg"
              onClick={onShowWalkInDetails}
            >
              {t('WALK_IN_ONLY')}
            </Button>
          )}
        </div>
      );

    case 'NO_AVAILABILITY':
    default:
      if (alternativeMenus.length > 0 && onSelectAlternativeMenu) {
        return (
          <AlternativeMenusMessage
            menus={alternativeMenus}
            onSelect={onSelectAlternativeMenu}
            t={t}
          />
        );
      }

      return (
        <div className="flex flex-col items-center py-8 px-6 max-w-md mx-auto">
          <p className="text-foreground font-medium text-lg mb-4 text-center">
            {t('NOT_AVAILABLE')}
          </p>
          <p className="text-foreground/70 text-sm text-center">
            {t('STRING_NO_RESULTS_CHANGE_PARAMETERS')}
          </p>
        </div>
      );
  }
}

function HorizontalTimeSlots({
  times,
  onSelect,
}: {
  times: string[];
  onSelect: (time: string) => void;
}) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollState = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 0);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  }, []);

  const scroll = useCallback((direction: 'left' | 'right') => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({ left: direction === 'left' ? -160 : 160, behavior: 'smooth' });
  }, []);

  return (
    <div className="relative group/scroll">
      {/* Desktop arrows */}
      {canScrollLeft && (
        <button
          type="button"
          onClick={() => scroll('left')}
          className="absolute left-0 top-1/2 -translate-y-1/2 z-10 hidden sm:flex items-center justify-center w-7 h-7 rounded-full bg-card border border-border shadow-sm hover:bg-accent"
        >
          <ChevronLeft size={14} className="text-foreground/70" />
        </button>
      )}
      {canScrollRight && (
        <button
          type="button"
          onClick={() => scroll('right')}
          className="absolute right-0 top-1/2 -translate-y-1/2 z-10 hidden sm:flex items-center justify-center w-7 h-7 rounded-full bg-card border border-border shadow-sm hover:bg-accent"
        >
          <ChevronRight size={14} className="text-foreground/70" />
        </button>
      )}

      <div
        ref={(el) => {
          (scrollRef as React.MutableRefObject<HTMLDivElement | null>).current = el;
          if (el) setTimeout(updateScrollState, 0);
        }}
        onScroll={updateScrollState}
        className="flex gap-2 overflow-x-auto scrollbar-hide px-1 py-1"
        style={{ overscrollBehaviorX: 'contain', WebkitOverflowScrolling: 'touch' }}
      >
        {times.map((time) => (
          <button
            key={time}
            type="button"
            onClick={() => onSelect(time)}
            className="flex-shrink-0 px-4 py-2 rounded-xl border border-border text-sm font-medium text-foreground hover:border-brand hover:text-brand transition-colors"
          >
            {time}
          </button>
        ))}
      </div>
    </div>
  );
}

function AlternativeMenusMessage({
  menus,
  onSelect,
  t,
}: {
  menus: AlternativeMenu[];
  onSelect: (menuId: number, time?: string) => void;
  t: (key: string, fallback?: string) => string;
}) {
  const [detailMenu, setDetailMenu] = useState<AlternativeMenu | null>(null);

  return (
    <>
      <div className="flex flex-col items-center py-8 px-6 max-w-md mx-auto">
        <p className="text-foreground font-medium text-lg mb-2 text-center">
          {t('TIME_NO_EXPERIENCE_AVAILABILITY')}
        </p>
        <p className="text-foreground/70 text-sm text-center mb-6">
          {t('TIME_ALTERNATIVE_EXPERIENCES')}
        </p>
        <div className="w-full space-y-4">
          {menus.map((menu) => {
            const hasDetail = !!(menu.description || menu.details);
            return (
              <div
                key={menu.id}
                className="w-full rounded-xl border border-border overflow-hidden p-3"
              >
                <div className="flex items-baseline justify-between gap-2 mb-2">
                  <p className="text-sm font-medium text-foreground truncate">{menu.name}</p>
                  {menu.price && (
                    <p className="text-xs text-brand flex-shrink-0">{menu.price}</p>
                  )}
                </div>
                {menu.description && (
                  <div className="mb-2">
                    <span className="text-xs text-foreground/50 line-clamp-2" dangerouslySetInnerHTML={{ __html: sanitizeHtml(menu.description) }} />
                    {hasDetail && (
                      <button
                        type="button"
                        onClick={() => setDetailMenu(menu)}
                        className="text-xs text-brand hover:text-brand-dark font-medium ml-1"
                      >
                        {t('NEW_HOME_VIEW_MORE', 'View more')}
                      </button>
                    )}
                  </div>
                )}
                {!menu.description && hasDetail && (
                  <div className="mb-2">
                    <button
                      type="button"
                      onClick={() => setDetailMenu(menu)}
                      className="text-xs text-brand hover:text-brand-dark font-medium"
                    >
                      {t('NEW_HOME_VIEW_MORE', 'View more')}
                    </button>
                  </div>
                )}
                <div>
                  <HorizontalTimeSlots
                    times={menu.times}
                    onSelect={(time) => onSelect(menu.id, time)}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <Dialog open={!!detailMenu} onOpenChange={(open) => { if (!open) setDetailMenu(null); }}>
        <DialogContent className="max-h-[85vh] overflow-y-auto bg-card">
          {detailMenu && (
            <>
              <DialogHeader>
                <DialogTitle>{detailMenu.name}</DialogTitle>
              </DialogHeader>

              {detailMenu.image && (
                <div
                  className="w-full h-40 rounded-lg bg-cover bg-center"
                  style={{ backgroundImage: `url(${detailMenu.image})` }}
                />
              )}

              {detailMenu.price && (
                <p className="text-lg font-semibold text-brand">{detailMenu.price}</p>
              )}

              {detailMenu.description && (
                <div
                  className="text-sm text-foreground/70 prose prose-sm max-w-none"
                  dangerouslySetInnerHTML={{ __html: sanitizeHtml(detailMenu.description) }}
                />
              )}

              {detailMenu.details && (
                <div
                  className="text-sm text-foreground/70 prose prose-sm max-w-none"
                  dangerouslySetInnerHTML={{ __html: sanitizeHtml(detailMenu.details) }}
                />
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

const AvailableTimesList = ({
  availableTimes,
  selectedTime,
  onSelectTime,
  loading = false,
  columnType,
  allowWaitingList = false,
  isWalkInDay = false,
  isWaitingListDay = false,
  onJoinWaitingList,
  centerHasOnlyPreferred = false,
  isAuthenticated = false,
  onPreferredLogin,
  alternativeMenus = [],
  onSelectAlternativeMenu,
  onShowWalkInDetails,
}: AvailableTimesListProps) => {
  // Aggregate state for the unit-level data-verify-* contract. These describe
  // what an agent could read to reason about the list; we intentionally include
  // a stable scalar (state) so dom-contract is satisfied in every branch.
  const itemCount = availableTimes.length;
  const hasSelection = selectedTime !== null && selectedTime !== undefined;
  const branch = loading
    ? "loading"
    : itemCount === 0
      ? "no-availability"
      : "list";

  if (loading) {
    return (
      <div
        className="flex flex-col gap-4 mb-6"
        role="status"
        aria-label="Loading time slots"
        {...verifyAttrs("AvailableTimesList", {
          branch,
          itemCount,
          isLoading: true,
          hasSelection,
        })}
      >
        {[...Array(skeletonCount)].map((_, i) => (
          <div key={i} className="w-full max-w-xs mx-auto">
            {/* Mirror TimeSlotButton dimensions exactly so the layout
                doesn't shift when real slots replace skeletons. */}
            <Skeleton className="w-full h-[60px] rounded-xl" />
          </div>
        ))}
      </div>
    );
  }

  if (availableTimes.length === 0) {
    const variant = getNoAvailabilityVariant(columnType, isWalkInDay, isWaitingListDay, centerHasOnlyPreferred);
    const message = (
      <NoAvailabilityMessage
        variant={variant}
        onJoinWaitingList={onJoinWaitingList}
        isAuthenticated={isAuthenticated}
        onPreferredLogin={onPreferredLogin}
        allowWaitingList={allowWaitingList}
        alternativeMenus={alternativeMenus}
        onSelectAlternativeMenu={onSelectAlternativeMenu}
        onShowWalkInDetails={onShowWalkInDetails}
      />
    );
    // DEV-only: wrap in a div carrying data-verify-* attrs so the dashboard +
    // matrix can read empty-state contract. In prod, return the bare message
    // — DOM-identical to the pre-verification implementation. The whole branch
    // tree-shakes via import.meta.env.DEV being a build-time constant.
    if (import.meta.env.DEV) {
      return (
        <div
          {...verifyAttrs("AvailableTimesList", {
            branch,
            itemCount,
            isLoading: false,
            hasSelection,
            variant,
            allowWaitingList: Boolean(allowWaitingList),
            isAuthenticated: Boolean(isAuthenticated),
            alternativeMenuCount: alternativeMenus.length,
          })}
        >
          {message}
        </div>
      );
    }
    return message;
  }

  return (
    <div
      className="flex flex-col gap-4 mb-6"
      role="radiogroup"
      aria-label="Available times"
      {...verifyAttrs("AvailableTimesList", {
        branch,
        itemCount,
        isLoading: false,
        hasSelection,
        selectedTime: selectedTime ?? null,
      })}
    >
      {availableTimes.map((slot) => (
        <div key={slot.time} className="flex flex-col items-center">
          <TimeSlotButton
            time={slot.time}
            selected={selectedTime === slot.time}
            onClick={() => onSelectTime(slot.time)}
            isPremium={slot.isPremium}
            type={slot.type}
            hasEventMenu={slot.hasEventMenu}
          />
        </div>
      ))}
    </div>
  );
};

export default AvailableTimesList;
